/**
 * @file stackedSection.component.tsx
 * @path apps/website/src/components/sections/stackedSection.component.tsx
 * @description UI component
 */

"use client";

import { audienceData } from "@/data";
import { Button } from "@sirbastians/ui";
import { motion, MotionValue, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import React, { useRef } from "react";
import { Link } from "react-router-dom";

// 1. Interface matching your Home.page.tsx mapping exactly
export interface StackedCardItem {
  id: string;
  number: string;
  title: string;
  features: { title: string; description: string }[];
  buttonText?: string;
  buttonLink?: string;
}

export const StackedSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const items = audienceData.map((audience, index) => ({
    id: `audience-${index}`,
    number: `0${index + 1}`, // Generates 01, 02, 03
    title: audience.title,
    // Pass the formatting data directly instead of a React content node
    features: [{ title: "", description: audience.description }],
    buttonText: audience.buttonText,
    buttonLink: audience.link,
  }));

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  if (!items || items.length === 0) return null;

  return (
    <div ref={containerRef} className="relative w-full">
      {items.map((item, index) => {
        const targetScale = 1 - (items.length - index) * 0.03;

        return (
          <StackedCard
            key={item.id}
            item={item}
            index={index}
            totalItems={items.length}
            progress={scrollYProgress}
            targetScale={targetScale}
          />
        );
      })}
    </div>
  );
};

// 2. Sub-component for individual cards
interface StackedCardProps {
  item: StackedCardItem;
  index: number;
  totalItems: number;
  progress: MotionValue<number>;
  targetScale: number;
}

const StackedCard: React.FC<StackedCardProps> = ({
  item,
  index,
  totalItems,
  progress,
  targetScale = 4,
}) => {
  const range = [index * (1 / totalItems), 1];
  const scale = useTransform(progress, range, [1, targetScale]);
  const topOffset = `calc(10vh + ${index * 150}px)`;

  return (
    <div
      className="w-full flex items-start justify-center sticky py-[10vh] px-4 md:px-8"
      style={{ top: topOffset }}
    >
      <motion.div
        style={{
          scale,
          transformOrigin: "top center",
        }}
        className="w-full max-w-5xl mx-auto flex flex-col bg-card overflow-hidden border border-border rounded-3xl p-6 md:p-12 relative"
      >
        {/* Header Section */}
        <div className="flex items-center justify-between mb-8 pb-4">
          <h2 className="text-xl md:text-3xl font-montserrat font-bold tracking-wider text-card-foreground">
            {item.title}
          </h2>

          <span
            className="text-5xl md:text-7xl font-light font-montserrat"
            style={{ color: "hsl(var(--accent))" }}
          >
            {item.number}
          </span>
        </div>

        {/* 3. YOUR NEW CONTENT STYLING */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 w-full pt-10 border-t border-black/5 dark:border-white/5">
          {item.features.map((feature, i) => (
            <div key={i} className="flex flex-col space-y-4 group">
              {/* Added a small check for title in case your mapped data leaves it empty */}
              {feature.title && (
                <h4 className="text-xl md:text-2xl font-bold text-foreground font-montserrat transition-colors group-hover:text-accent">
                  {feature.title}
                </h4>
              )}

              <p className="text-muted-foreground leading-relaxed text-sm md:text-base max-w-md">
                {feature.description}
              </p>

              {/* CTA Render */}
              {i === item.features.length - 1 &&
                item.buttonText &&
                item.buttonLink && (
                  <div className="pt-6 flex items-start">
                    <Button
                      variant="accent"
                      size="lg"
                      asChild
                      className="rounded-full shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 group/btn"
                    >
                      <Link
                        to={item.buttonLink}
                        className="flex items-center gap-2"
                      >
                        {item.buttonText}
                        {/* Using group-hover/btn ensures the arrow only moves when hovering the button itself */}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                )}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};
